import React, { useEffect } from "react"
import { Modal } from "@bancacfplus/Modal"
import { Loader } from "../../components/Loader"
import { X } from "react-feather"
import { useUsersInfo } from "../../pages/contracts-view/hooks/useUsersInfo"
import { loadEnvVars } from "../../utils/loadEnvVars"
import style from "./Cobrowse.module.scss"
import { createRoot } from "react-dom/client"
import { getDocsByClientSource } from "../../utils/getDocsByClientSource"
import { brand } from "../../consts/brand"

declare global {
  interface Window {
    CobrowseIO: {
      confirmSession: unknown
      createSessionCode: unknown
      license?: string
      api?: string
      customData?: {
        user_id: string
        user_name: string
        user_email: string
        user_tax_code: string
        company_tax_code: string
      }
      client?: () => Promise<void>
      start?: () => void
    }
  }
}

type CobrowseModalProps = {
  code: string | null
  closeHandler: React.Dispatch<React.SetStateAction<string | null>>
  loading: boolean
  error: Error | null
  generateCode: () => Promise<void>
}

type ConsentModalProps = {
  onAllow: () => void
  onDeny: () => void
}

export function showConsentModal(): Promise<boolean> {
  return new Promise(resolve => {
    const container = document.createElement("div")
    document.body.appendChild(container)
    const root = createRoot(container)

    const cleanup = () => {
      root.unmount()
      container.remove()
    }

    const handleAllow = () => {
      resolve(true)
      cleanup()
    }

    const handleDeny = () => {
      resolve(false)
      cleanup()
    }

    root.render(<ConsentModal onAllow={handleAllow} onDeny={handleDeny} />)
  })
}

export const ConsentModal: React.FC<ConsentModalProps> = ({ onAllow, onDeny }) => {
  return (
    <Modal id="consent-dialog" hash={false} closehandler={onDeny} close="dark" type="cta" opened={true}>
      <h2>Richiesta di assistenza</h2>
      <p className={style.description}>
        Un agente di {brand.name} vorrebbe utilizzare temporaneamente questa pagina con te.
      </p>
      <p>
        Ti ricordiamo che il servizio è gratuito e può essere utilizzato esclusivamente da PC. La condivisione è
        limitata alla presente pagina e la sessione non verrà registrata.
      </p>
      <p>
        Ti informiamo che l’operatore Banca agisce in qualità di Responsabile del trattamento dei dati. Per maggiori
        dettagli, puoi consultare la nostra{" "}
        <a href={getDocsByClientSource().informativaPrivacy} target="_blank" rel="noreferrer">
          Informativa Privacy {brand.name}
        </a>
      </p>
      <p>Vuoi procedere con la condivisione?</p>
      <div className={style.ctaContainer}>
        <button onClick={onAllow} className={style.button}>
          Accetta
        </button>
        <button onClick={onDeny} className={style.buttonSecondary}>
          Rifiuta
        </button>
      </div>
    </Modal>
  )
}

export const CobrowseModal: React.FC<CobrowseModalProps> = ({
  code,
  closeHandler,
  loading,
  error,
  generateCode
}): React.JSX.Element => {
  const { data: userInfo, loading: loadingUserInfo, error: errorUserInfo } = useUsersInfo()
  const { cobrowseKey } = loadEnvVars()

  useEffect(() => {
    if (error || errorUserInfo) return

    window.CobrowseIO = window.CobrowseIO || []
    window.CobrowseIO.license = cobrowseKey
    window.CobrowseIO.api = "https://eu.cobrowse.io"
    window.CobrowseIO.customData = {
      user_id: userInfo?.user.contactId || "",
      user_name: `${userInfo?.user.firstName || ""} ${userInfo?.user.lastName || ""}`.trim(),
      user_email: userInfo?.user.email || "",
      user_tax_code: userInfo?.user.taxCode || "",
      company_tax_code: userInfo?.user.companyTaxCode || ""
    }

    if (!window.CobrowseIO.client) return

    void window.CobrowseIO.client().then(() => {
      window.CobrowseIO.start && window.CobrowseIO.start()
    })

    window.CobrowseIO.confirmSession = () => {
      closeHandler(null)
      return showConsentModal()
    }
  }, [closeHandler, cobrowseKey, error, errorUserInfo, userInfo])

  if (errorUserInfo || error) return <></>

  return (
    <Modal
      id="codice-assistenza"
      hash={false}
      closehandler={() => closeHandler(null)}
      close="dark"
      type="cta"
      opened={!!code || !!loading}
    >
      {loading || loadingUserInfo ? (
        <Loader />
      ) : (
        <>
          <h2>Hai bisogno di supporto in piattaforma?</h2>
          <div>
            <p>
              Chiama il numero <strong>{brand.supportPhone}</strong> e fornisci il codice che trovi di seguito per
              avviare una sessione di navigazione assistita con un nostro operatore.
            </p>
            <p>Siamo qui per aiutarti!</p>
            <h2 className={style.assistanceCode}>
              <strong>{code}</strong>
            </h2>
            <button className={style.link} onClick={generateCode}>
              hai bisogno di un altro codice? clicca qui
            </button>
          </div>
        </>
      )}
    </Modal>
  )
}
